import { createNoise2D } from 'simplex-noise';
import {
  TERRAIN_WIDTH, TERRAIN_DEPTH,
  TERRAIN_SEGMENTS_X, TERRAIN_SEGMENTS_Z,
  TERRAIN_MAX_HEIGHT, TERRAIN_EDGE_FALLOFF,
} from '../constants.js';

// ─── Terrain Archetypes ───

type TerrainArchetype = 'rolling_hills' | 'mountain_range' | 'canyon' | 'mesa' | 'cratered';

const ARCHETYPES: TerrainArchetype[] = [
  'rolling_hills',
  'mountain_range',
  'canyon',
  'mesa',
  'cratered',
];

interface FBmParams {
  octaves: number;
  baseFreq: number;
  persistence: number; // amplitude decay per octave (0..1)
  lacunarity: number;  // frequency multiplier per octave
  amplitude: number;   // overall scale
}

const ARCHETYPE_PARAMS: Record<TerrainArchetype, FBmParams> = {
  rolling_hills: {
    octaves: 6,
    baseFreq: 0.006,
    persistence: 0.45,
    lacunarity: 2.1,
    amplitude: 0.65,
  },
  mountain_range: {
    octaves: 7,
    baseFreq: 0.005,
    persistence: 0.52,
    lacunarity: 2.0,
    amplitude: 0.90,
  },
  canyon: {
    octaves: 6,
    baseFreq: 0.007,
    persistence: 0.48,
    lacunarity: 2.0,
    amplitude: 0.75,
  },
  mesa: {
    octaves: 5,
    baseFreq: 0.005,
    persistence: 0.40,
    lacunarity: 2.2,
    amplitude: 0.70,
  },
  cratered: {
    octaves: 6,
    baseFreq: 0.006,
    persistence: 0.42,
    lacunarity: 2.1,
    amplitude: 0.55,
  },
};

// ─── Utility ───

/** Smoothstep: 0→0 at edge0, 1→1 at edge1 */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** fBm (fractal Brownian motion) with standard additive octaves */
function fbm(
  noise: (x: number, y: number) => number,
  wx: number, wz: number,
  p: FBmParams,
): number {
  let value = 0;
  let amp = 1;
  let freq = p.baseFreq;
  let maxAmp = 0;

  for (let i = 0; i < p.octaves; i++) {
    value += noise(wx * freq, wz * freq) * amp;
    maxAmp += amp;
    amp *= p.persistence;
    freq *= p.lacunarity;
  }

  return (value / maxAmp) * p.amplitude;
}

/** Ridged fBm — absolute-value noise for sharp ridges */
function ridgedFbm(
  noise: (x: number, y: number) => number,
  wx: number, wz: number,
  p: FBmParams,
): number {
  let value = 0;
  let amp = 1;
  let freq = p.baseFreq;
  let maxAmp = 0;
  let weight = 1;

  for (let i = 0; i < p.octaves; i++) {
    let signal = noise(wx * freq, wz * freq);
    signal = 1.0 - Math.abs(signal); // ridge
    signal *= signal; // sharpen ridges
    signal *= weight;
    weight = Math.max(0, Math.min(1, signal * 2));

    value += signal * amp;
    maxAmp += amp;
    amp *= p.persistence;
    freq *= p.lacunarity;
  }

  return (value / maxAmp) * p.amplitude;
}

/** Terrace noise — quantize into steps then smooth slightly */
function terrace(value: number, steps: number): number {
  const scaled = value * steps;
  const stepped = Math.floor(scaled);
  const frac = scaled - stepped;
  // Smooth the transition between steps
  const smooth = smoothstep(0.3, 0.7, frac);
  return (stepped + smooth) / steps;
}

// ─── Edge Falloff Mask ───

function edgeFalloff(u: number, v: number): number {
  const falloff = TERRAIN_EDGE_FALLOFF;
  // Distance from each edge in UV (0..1)
  const fx = Math.min(u / falloff, (1 - u) / falloff, 1);
  const fz = Math.min(v / falloff, (1 - v) / falloff, 1);
  // Smoothstep for gradual taper
  return smoothstep(0, 1, fx) * smoothstep(0, 1, fz);
}

// ─── Spawn Zone Flattening ───

function spawnFlatten(wx: number, wz: number): number {
  // Red team spawns near left, blue near right
  const redCX = -(TERRAIN_WIDTH / 2) + 40;
  const blueCX = (TERRAIN_WIDTH / 2) - 40;
  const spawnRadius = 25;

  // Distance to each spawn center (XZ plane)
  const dRed = Math.sqrt((wx - redCX) ** 2 + wz * wz);
  const dBlue = Math.sqrt((wx - blueCX) ** 2 + wz * wz);
  const dMin = Math.min(dRed, dBlue);

  if (dMin >= spawnRadius) return 1; // no flattening
  // Smoothstep from full terrain at edge to 60% height reduction at center
  return 0.4 + 0.6 * smoothstep(0, spawnRadius, dMin);
}

// ─── Main Generation ───

export function generateTerrainHeightmap(seed: number): Float32Array {
  const vertsX = TERRAIN_SEGMENTS_X + 1;
  const vertsZ = TERRAIN_SEGMENTS_Z + 1;
  const heightmap = new Float32Array(vertsX * vertsZ);

  // Seeded PRNG
  let s = seed;
  const prng = (): number => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };

  // Create multiple noise instances for different purposes
  const noise = createNoise2D(prng);
  const warpNoise = createNoise2D(prng);
  const detailNoise = createNoise2D(prng);

  // Select archetype from seed
  const archetype = ARCHETYPES[seed % ARCHETYPES.length];
  const params = ARCHETYPE_PARAMS[archetype];

  const cellW = TERRAIN_WIDTH / TERRAIN_SEGMENTS_X;
  const cellD = TERRAIN_DEPTH / TERRAIN_SEGMENTS_Z;
  const halfW = TERRAIN_WIDTH / 2;
  const halfD = TERRAIN_DEPTH / 2;
  const maxH = TERRAIN_MAX_HEIGHT;

  for (let z = 0; z < vertsZ; z++) {
    for (let x = 0; x < vertsX; x++) {
      const wx = x * cellW - halfW;
      const wz = z * cellD - halfD;
      const u = x / (vertsX - 1); // 0..1
      const v = z / (vertsZ - 1);

      // Domain warping — distort coordinates for more organic shapes
      const warpStrength = 30;
      const warpedX = wx + warpNoise(wx * 0.004, wz * 0.004) * warpStrength;
      const warpedZ = wz + warpNoise(wz * 0.004 + 100, wx * 0.004 + 100) * warpStrength;

      let h = 0;

      switch (archetype) {
        case 'rolling_hills': {
          h = fbm(noise, warpedX, warpedZ, params);
          // Gentle base offset to keep most terrain above zero
          h = h * maxH + maxH * 0.35;
          // Smooth compression at low end (no hard clamp)
          if (h < 2) h = 2 + (h - 2) * 0.3;
          break;
        }

        case 'mountain_range': {
          // Ridged noise for dramatic peaks + regular fbm for base
          const base = fbm(noise, warpedX, warpedZ, { ...params, amplitude: 0.3 });
          const ridges = ridgedFbm(noise, warpedX, warpedZ, params);
          // Center-weighted: peaks are taller in the middle of the map
          const centerWeight = 1 - Math.abs(wz / halfD) * 0.4;
          h = (base * 0.3 + ridges * 0.7) * maxH * centerWeight + maxH * 0.15;
          break;
        }

        case 'canyon': {
          // Two elevated sides with a channel through the center
          const baseH = fbm(noise, warpedX, warpedZ, params);
          // Channel carved along the X axis through the center (Z near 0)
          const channelDepth = smoothstep(30, 0, Math.abs(wz + warpNoise(wx * 0.008, 50) * 25));
          h = baseH * maxH + maxH * 0.5;
          h -= channelDepth * maxH * 0.55;
          // Add detail in the canyon walls
          h += detailNoise(warpedX * 0.03, warpedZ * 0.03) * 3;
          break;
        }

        case 'mesa': {
          // Terraced plateaus with steep edges
          const baseH = fbm(noise, warpedX, warpedZ, params);
          // Terrace into 5 distinct levels
          const terraced = terrace(baseH * 0.5 + 0.5, 5);
          h = terraced * maxH * 0.85 + maxH * 0.1;
          // Soften edges slightly with detail noise
          h += detailNoise(warpedX * 0.02, warpedZ * 0.02) * 2;
          break;
        }

        case 'cratered': {
          // Moderate base terrain + pre-applied random craters
          h = fbm(noise, warpedX, warpedZ, params);
          h = h * maxH + maxH * 0.3;

          // Procedural craters from a secondary noise pass
          // Use deterministic pseudo-random craters based on seed
          let craterS = seed * 7919;
          const numCraters = 8 + (seed % 5);
          for (let ci = 0; ci < numCraters; ci++) {
            craterS = (craterS * 1664525 + 1013904223) & 0xffffffff;
            const ccx = ((craterS >>> 0) / 0xffffffff - 0.5) * TERRAIN_WIDTH * 0.7;
            craterS = (craterS * 1664525 + 1013904223) & 0xffffffff;
            const ccz = ((craterS >>> 0) / 0xffffffff - 0.5) * TERRAIN_DEPTH * 0.7;
            craterS = (craterS * 1664525 + 1013904223) & 0xffffffff;
            const cradius = 8 + ((craterS >>> 0) / 0xffffffff) * 15;
            const cdepth = cradius * 0.5;

            const cdx = wx - ccx;
            const cdz = wz - ccz;
            const cdist = Math.sqrt(cdx * cdx + cdz * cdz);
            if (cdist < cradius) {
              const f = 1 - cdist / cradius;
              h -= cdepth * f * f;
              // Rim
              if (cdist > cradius * 0.7) {
                const rimF = (cdist - cradius * 0.7) / (cradius * 0.3);
                h += cdepth * 0.15 * Math.sin(rimF * Math.PI);
              }
            }
          }
          if (h < -5) h = -5 + (h + 5) * 0.2; // soft floor
          break;
        }
      }

      // Edge falloff — taper terrain at map boundaries
      h *= edgeFalloff(u, v);

      // Spawn zone flattening — reduce height variation near team spawns
      h *= spawnFlatten(wx, wz);

      // Final clamp — allow slight valleys but not extreme negative
      h = Math.max(-5, Math.min(maxH + 5, h));

      heightmap[z * vertsX + x] = h;
    }
  }

  return heightmap;
}

/** Get the archetype name for a given seed (for UI display). */
export function getTerrainArchetype(seed: number): TerrainArchetype {
  return ARCHETYPES[seed % ARCHETYPES.length];
}